// @owma/common-web/utils/core — 뷰어-안전(host 앱 의존 없는) 비-UI 헬퍼.
// email-thread-viewer 는 반드시 이 모듈만 import 한다(barrel index.ts 는 owma 앱 결합 코드를
// 포함하므로 뷰어 모듈 그래프에 넣지 말 것 — platform(officemail-platform) 스왑 시 해석 불가).
// resolveTimeFormats 는 platform 판 채택(moment.locale 동기화 + ko/ja 12시간제 재배치).
import moment from 'moment'

import i18n from 'i18n'
import styleToInlineStyle, { styleToInlineStyleAsync } from '../libs/style-to-inline'
import type { BakePauseGate } from '../libs/style-to-inline'
import { PSEUDO_STYLE_MARKER } from '../libs/style-to-inline/markers'

// ─── emailAddress ───────────────────────────────────────────────────────────

export interface EmailAddress {
  user: string
  host: string
  address: string
}

export function emailAddress(emailStr: string | null | undefined): EmailAddress | null {
  if (emailStr) {
    const pos = emailStr.indexOf('@')
    if (pos >= 0) {
      return {
        user: emailStr.substring(0, pos),
        host: emailStr.substring(pos + 1),
        address: emailStr,
      }
    }
  }
  return null
}

// ─── composed DOM (shadow 경계) ─────────────────────────────────────────────

// shadow 경계를 넘는 DOM 탐색. 메일 본문이 `.htmlMailBody` host 의 open shadow root 안에 mount 되면
// (viewer bodyIsolation 'shadow') parentElement·contains 가 shadow root 에서 멈춘다. shadow 가 없으면
// 두 함수는 parentElement·contains 와 같은 결과다.

/** 부모 요소. shadow root 바로 아래 노드면 그 host 를 돌려준다. */
export function composedParentElement(node: Node): Element | null {
  if (node.parentElement != null) return node.parentElement
  const parent = node.parentNode
  return parent instanceof ShadowRoot ? parent.host : null
}

/** node 가 container 자신이거나 그 composed 자손인지 — shadow root 를 만나면 host 를 따라 올라간다. */
export function composedContains(container: Node, node: Node | null): boolean {
  for (let cur = node; cur != null; cur = cur instanceof ShadowRoot ? cur.host : cur.parentNode) {
    if (cur === container) return true
  }
  return false
}

// ─── findScrollableParent ────────────────────────────────────────────────────

export function findScrollableParent(element: HTMLElement): HTMLElement | HTMLBodyElement {
  // shadow 본문(bodyIsolation 'shadow') 안에서 시작해도 host 를 넘어 scroll container 를 찾는다.
  let parent = composedParentElement(element) as HTMLElement | null

  const canOverflow = (overflow: string): boolean => {
    return overflow !== 'visible' && overflow !== 'clip'
  }

  const isScrollable = (el: HTMLElement): boolean => {
    if (el.scrollHeight > el.clientHeight) {
      const style = window.getComputedStyle(el)
      return canOverflow(style.overflow) || canOverflow(style.overflowY)
    }
    return false
  }

  while (parent && parent !== document.body) {
    if (isScrollable(parent)) {
      return parent
    }

    parent = composedParentElement(parent) as HTMLElement | null
  }

  return document.body
}

// ─── scrollIntoViewIfNeeded (owma 판 유지 — divergence 주석 있는 항목) ──────

interface ScrollOptions {
  behavior?: 'auto' | 'smooth'
}

export function scrollIntoViewIfNeeded(elem: HTMLElement | null, options?: ScrollOptions): void {
  if (elem == null) {
    return
  }

  function isElementInViewport(
    element: HTMLElement,
    scrollContainer: HTMLElement | Window,
  ): boolean {
    const OFFSET = 100
    const elementRect = element.getBoundingClientRect()

    const getBoundary = (): { top: number; bottom: number } => {
      if (scrollContainer === window) {
        return {
          top: OFFSET,
          bottom: window.innerHeight - OFFSET,
        }
      }

      const containerRect = (scrollContainer as HTMLElement).getBoundingClientRect()
      return {
        top: containerRect.top + OFFSET,
        bottom: containerRect.bottom - OFFSET,
      }
    }

    const boundary = getBoundary()
    return elementRect.top <= boundary.bottom && elementRect.bottom >= boundary.top
  }

  const scrollContainer = findScrollableParent(elem)

  if (isElementInViewport(elem, scrollContainer)) {
    return
  }

  const elementRect = elem.getBoundingClientRect()
  const containerRect = scrollContainer.getBoundingClientRect()

  scrollContainer.scrollTo({
    top: scrollContainer.scrollTop + elementRect.top - containerRect.top,
    behavior: options != null && options.behavior != null ? options.behavior : 'auto',
  })
}

// ─── resolveTimeFormats (platform 판 채택 — moment.locale 동기화 + ko/ja 12시간제) ──

export interface TimeFormatInput {
  dateFormat?: string | null
  timeFormat?: string | null
  dateTimeFormat?: string | null
}

export interface ResolvedTimeFormats {
  dateFormat: string
  timeFormat: string
  dateTimeFormat: string
}

export function resolveTimeFormats(input: TimeFormatInput): ResolvedTimeFormats {
  const { dateFormat: dFormat, timeFormat: tFormat, dateTimeFormat: dtFormat } = input

  const dateFmt = dFormat || 'L'

  // desktop i18n 은 short 코드("ko"/"ja"/"en")를 쓰므로 base-lang 으로 정규화한다
  // (web 은 "ko-KR" 등 long 코드 — 둘 다 지원). `dateTimeFmt` 가 이미 주어졌는지와 무관하게
  // 항상 산출한다 — 아래 12시간제 재배치가 그 분기 밖에서도 적용돼야 하기 때문이다.
  const baseLang = (i18n.language || 'en').split('-')[0]
  // 뷰어 moment 인스턴스의 locale 을 언어에 맞춘다(요일 이름 현지화 + 오전/오후 토큰 텍스트).
  // setSettings/languageChanged 마다 resolveTimeFormats 가 호출되므로 여기서 sync 를 보장한다.
  moment.locale(baseLang === 'ko' || baseLang === 'ja' ? baseLang : 'en')

  // ko/ja 는 오전/오후가 시각 앞에 온다. 12시간제 패턴("h:mm A")만 재배치 대상.
  // 24시간제("HH:mm")와 "LT" 폴백(moment 가 로케일별 순서를 이미 아는 토큰)은 그대로 둔다.
  let timeFmt = tFormat || 'LT'
  if (timeFmt === 'h:mm A') {
    if (baseLang === 'ko') timeFmt = 'A h:mm'
    else if (baseLang === 'ja') timeFmt = 'Ah:mm'
  }

  let dateTimeFmt = dtFormat ?? null
  if (dateTimeFmt == null) {
    if (tFormat == null && dFormat == null) {
      dateTimeFmt = 'LLLL'
    } else {
      switch (baseLang) {
        case 'ko':
        case 'ja':
          dateTimeFmt = `${dateFmt} (ddd) ${timeFmt}`
          break
        default:
          dateTimeFmt = `ddd, ${dateFmt} ${timeFmt}`
          break
      }
    }
  }

  return { dateFormat: dateFmt, timeFormat: timeFmt, dateTimeFormat: dateTimeFmt }
}

// ─── timeFormat ───────────────────────────────────────────────────────────────

export function timeFormat(
  time: string | Date | number | null | undefined,
  formatStr: string,
): string {
  if (time == null || (typeof time === 'string' && time.length === 0)) {
    return '-'
  }

  let utcTime: string
  if (time instanceof Date) {
    utcTime = time.toISOString()
  } else if (typeof time === 'number') {
    utcTime = new Date(time).toISOString()
  } else {
    utcTime = time
    if (time.length != null && time.indexOf('Z') === -1 && time.indexOf('+') === -1) {
      utcTime = `${time}Z`
    }
  }
  return moment(utcTime).format(formatStr)
}

// ─── getSafeHTML 전이 헬퍼들 ─────────────────────────────────────────────────

// getSafeHTML 호출별 독립 iframe 생성.
function createTempHiddenIFrame(): HTMLIFrameElement {
  const hiddenIFrame = document.createElement('iframe')
  hiddenIFrame.style.visibility = 'hidden'
  hiddenIFrame.style.position = 'absolute'
  hiddenIFrame.style.bottom = '0'
  hiddenIFrame.style.left = '0'
  hiddenIFrame.style.height = '1px'
  document.body.appendChild(hiddenIFrame)
  return hiddenIFrame
}

/**
 * doc.write 로 iframe 에 주입되기 전 메일 본문 HTML 을 무력화한다.
 *
 * 메일 본문 iframe 처리 정책은 호출부가 `mailBodyIframeSanitizer` 로 주입한다(opt-in). 미전달이면
 * iframe 순회 자체를 skip — 일반 브라우저(web)는 iframe 을 그대로 두면 되고(자동 src 로드가 OS
 * 브라우저로 새지 않으므로), 뷰어(email-thread-viewer)만 settings 기반 sanitizer 를 주입해 자체
 * 서버(officemail) origin 외 iframe 을 제거한다. desktop(Tauri)은 외부 iframe 의 자동 src 로드가
 * 메인 webview on_navigation 을 타고 OS 브라우저로 새는 문제(OMD-210) 때문에 뷰어 settings
 * (domainsForIFrame)로 동일 sanitizer 를 주입한다.
 * → sanitizer 생성: packages/email-thread-viewer/src/EmailView/mailBodyIframeSanitizer.ts.
 */
export function sanitizeHtmlForIframeWrite(
  doc: Document,
  mailBodyIframeSanitizer?: (iframe: HTMLIFrameElement) => void,
): void {
  doc.querySelectorAll('script, noscript').forEach((el) => el.remove())
  // 미전달/undefined 면 iframe 순회를 skip (기존 web no-op 과 동일 동작). 지정 시 호출부 정책 적용.
  if (mailBodyIframeSanitizer != null) {
    doc.querySelectorAll('iframe').forEach((el) => mailBodyIframeSanitizer(el as HTMLIFrameElement))
  }
  doc.querySelectorAll('*').forEach((el) => {
    for (let i = el.attributes.length - 1; i >= 0; i--) {
      const attrName = el.attributes[i].name
      if (attrName.length > 2 && attrName.toLowerCase().startsWith('on')) {
        el.removeAttribute(attrName)
      }
    }
    if (el.hasAttribute(PSEUDO_STYLE_MARKER)) {
      el.removeAttribute(PSEUDO_STYLE_MARKER)
    }
    const href = el.getAttribute('href')
    if (href != null && /^\s*javascript:/i.test(href)) {
      el.removeAttribute('href')
    }
    const src = el.getAttribute('src')
    if (src != null && /^\s*javascript:/i.test(src)) {
      el.removeAttribute('src')
    }
  })
}

export interface CSSValue {
  type: number
  number?: number
  unit?: string
  realUnit?: string
  isRel?: boolean
  value: string
}

export function getCSSValue(str: string, isAttr?: boolean, remSize?: number): CSSValue {
  const matchList = str.match(/^(-{0,1}\d+[.]*\d*|\d*[.]*\d+)/)
  if (matchList != null && matchList.length > 0) {
    const numStr = matchList[0]
    let unit = str.substring(numStr.length)
    let num = 0
    if (numStr.indexOf('.') > -1) {
      num = parseFloat(numStr)
    } else {
      num = parseInt(numStr)
    }
    switch (unit) {
      case 'rem': {
        const remNum = remSize != null ? remSize : 16
        num = num * remNum
        unit = 'px'
        break
      }
      case 'pt':
        num = (num * 4) / 3
        unit = 'px'
        break
      case 'in':
        num = num * 96
        unit = 'px'
        break
      case 'pc':
        num = num * 16
        unit = 'px'
        break
      case 'cm':
        num = num * 37.79
        unit = 'px'
        break
      case 'mm':
        num = num * 3.779
        unit = 'px'
        break
    }
    if (isAttr == true && unit == '') {
      unit = 'px'
    }
    return {
      type: 1,
      number: num,
      unit: unit.length == 0 ? 'px' : unit,
      realUnit: unit,
      isRel: unit != 'px',
      value: str,
    }
  }
  return {
    type: 0,
    value: str,
  }
}

export const cssImportToLink = (styles: StyleSheetList | CSSStyleSheet[], doc: Document): void => {
  Array.from(styles).forEach((style) => {
    const sheet = (style as any).sheet || style
    if (sheet.cssRules == null) {
      console.error(' --- invalid stylesheet : ', sheet)
    } else if (sheet.href == null || sheet.href.length == 0) {
      let changed = false
      let lastLink: HTMLLinkElement | null = null
      for (let i = sheet.cssRules.length - 1; i >= 0; i -= 1) {
        const rule = sheet.cssRules[i]
        const ruleType = rule.constructor.name
        if (ruleType === 'CSSImportRule') {
          sheet.deleteRule(i)
          const extPos = (rule as any).href.lastIndexOf('.')
          let isFont = false
          if (extPos > -1) {
            const ext = (rule as any).href.substring(extPos + 1).toLowerCase()
            if (
              ext === 'woff2' ||
              ext === 'woff' ||
              ext === 'otf' ||
              ext === 'ttf' ||
              ext === 'eot'
            ) {
              isFont = true
            }
          }
          const link = doc.createElement('link')
          link.rel = 'preconnect'
          if (isFont === true) {
            link.as = 'font'
          }
          link.href = (rule as any).href
          if (doc.head.contains(sheet.ownerNode) === true) {
            doc.head.insertBefore(link, sheet.ownerNode)
          } else {
            if (lastLink == null) {
              doc.head.appendChild(link)
            } else {
              doc.head.insertBefore(link, lastLink)
            }
            lastLink = link
          }
          changed = true
        } else if (ruleType === 'CSSFontFaceRule') {
          changed = true
        }
      }
      if (changed === true) {
        let styleText = '\n/* modified by Officemail */\n'
        for (let i = 0; i < sheet.cssRules.length; i++) {
          const rule = sheet.cssRules[i]
          let cssText = rule.cssText
          if (rule.constructor.name === 'CSSFontFaceRule') {
            if (/font-display\s*:/i.test(cssText)) {
              cssText = cssText.replace(/font-display\s*:[^;}]*;?/i, 'font-display: swap;')
            } else {
              cssText = cssText.replace(/}\s*$/, '  font-display: swap;\n}')
            }
          }
          styleText += cssText + '\n'
        }
        // NOTE: ownerNode is a <style> element — setting its text content
        // replaces existing stylesheet rules safely (no untrusted HTML involved).
        sheet.ownerNode.innerHTML = styleText
      }
    } else {
      console.log(' --- external sheet : ', sheet.href)
    }
  })
}

// 순수 prefers-color-scheme 단독 media condition 매칭(앞의 [only] screen/all and 허용).
// 복합 조건(콤마 목록, width 결합, not, 중첩 @media/@supports)은 1차 범위에서 처리하지 않는다
// — 후속: condition 에서 prefers-color-scheme 항만 제거해 재합성하는 2차 처리.
const PURE_COLOR_SCHEME_CONDITION =
  /^(?:(?:only\s+)?(?:screen|all)\s+and\s+)?\(\s*prefers-color-scheme\s*:\s*(dark|light)\s*\)$/i

// 이메일이 자체 선언한 prefers-color-scheme 테마 분기를 live CSSOM 에서 제거해 bake 결과를
// light 기준으로 고정한다. bake iframe 은 OS 의 prefers-color-scheme 을 그대로 평가하므로,
// 그대로 두면 OS 다크모드 사용자는 dark 분기가 inline 으로 bake 된다. dark @media 는 통째로
// 삭제하고, light @media 는 내부 규칙을 해당 위치에 펼쳐(cascade 순서 보존) 항상 적용되게 한다.
// 다크 표시는 뷰어의 darkmode 모듈(mailview)이 전담한다. adoptNode 이후의 live 문서에서
// 호출해야 하며(CSSOM-only 변경은 adoptNode 시 텍스트 기준 재생성으로 유실됨), bake 파이프라인
// (getRuleMap/의사요소 수집)은 live CSSOM 만 소비하므로 텍스트 재직렬화는 필요 없다.
const removePrefersColorSchemeRules = (doc: Document): void => {
  for (const sheet of Array.from(doc.styleSheets)) {
    // external sheet(href 보유)는 대상 아님. cross-origin cssRules 접근 실패도 skip.
    if (sheet.href != null && sheet.href.length > 0) continue
    let rules: CSSRuleList
    try {
      rules = sheet.cssRules
    } catch {
      continue
    }
    // 역순 순회: deleteRule/insertRule 로 인한 인덱스 밀림이 미처리 구간(i 미만)에 영향 없음.
    for (let i = rules.length - 1; i >= 0; i -= 1) {
      const rule = rules[i]
      // iframe 문서의 rule 은 다른 realm 소유라 instanceof 판별이 불가 — constructor.name 사용.
      if (rule.constructor.name !== 'CSSMediaRule') continue
      const match = PURE_COLOR_SCHEME_CONDITION.exec((rule as CSSMediaRule).conditionText)
      if (match == null) continue
      if (match[1].toLowerCase() === 'light') {
        // 내부 규칙을 media rule 이 있던 index 에 순서대로 삽입 → 원래 cascade 순서가 보존되어
        // light 선호 브라우저가 계산했을 결과와 동일해진다.
        const innerRules = Array.from((rule as CSSMediaRule).cssRules)
        let insertPos = i
        for (const innerRule of innerRules) {
          try {
            sheet.insertRule(innerRule.cssText, insertPos)
            insertPos += 1
          } catch {
            // 최상위 삽입 불가 규칙(@import 위치 제약 등)은 건너뛴다.
          }
        }
        // 삽입 성공 수만큼 뒤로 밀린 media rule 을 삭제.
        sheet.deleteRule(insertPos)
      } else {
        sheet.deleteRule(i)
      }
    }
  }
}

export const shorthandStyleMap: Record<string, string> = {
  'font-variant-alternates': 'font-variant',
  'font-variant-caps': 'font-variant',
  'font-variant-east-asian': 'font-variant',
  'font-variant-emoji': 'font-variant',
  'font-variant-ligatures': 'font-variant',
  'font-variant-numeric': 'font-variant',
  'font-variant-position': 'font-variant',
  'border-color': 'border',
  'border-style': 'border',
  'border-width': 'border',
  'border-top': 'border',
  'border-left': 'border',
  'border-right': 'border',
  'border-bottom': 'border',
  'border-top-color': 'border-top',
  'border-top-style': 'border-top',
  'border-top-width': 'border-top',
  'border-left-color': 'border-left',
  'border-left-style': 'border-left',
  'border-left-width': 'border-left',
  'border-right-color': 'border-right',
  'border-right-style': 'border-right',
  'border-right-width': 'border-right',
  'border-bottom-color': 'border-bottom',
  'border-bottom-style': 'border-bottom',
  'border-bottom-width': 'border-bottom',
  'padding-left': 'padding',
  'padding-right': 'padding',
  'padding-top': 'padding',
  'padding-bottom': 'padding',
  'padding-block-start': 'padding-block',
  'padding-block-end': 'padding-block',
  'padding-inline-start': 'padding-inline',
  'padding-inline-end': 'padding-inline',
  'margin-left': 'margin',
  'margin-right': 'margin',
  'margin-top': 'margin',
  'margin-bottom': 'margin',
  'margin-block-start': 'margin',
  'margin-block-end': 'margin',
  'margin-inline-start': 'margin',
  'margin-inline-end': 'margin',
  'text-decoration-line': 'text-decoration',
  'text-decoration-style': 'text-decoration',
  'text-decoration-color': 'text-decoration',
  'text-decoration-thickness': 'text-decoration',
}

// ─── getUniqueKey / deleteUniqueKeyName ─────────────────────────────────────

interface UniqueKeyValue {
  lastTime: number
  idx: number
}

const uniqueKeyMap: Record<string, UniqueKeyValue> = {}

export function deleteUniqueKeyName(name: string): void {
  delete uniqueKeyMap[name]
}

export function getUniqueKey(name: string): string {
  if (uniqueKeyMap[name] == null) {
    uniqueKeyMap[name] = {
      lastTime: -1,
      idx: 1,
    }
  }

  const valMgr = uniqueKeyMap[name]

  let keyStr: string
  const curTime = Date.now()
  if (valMgr.lastTime !== curTime) {
    keyStr = `${curTime}`
    valMgr.lastTime = curTime
    valMgr.idx = 1
  } else {
    keyStr = `${curTime}_${valMgr.idx}`
    valMgr.idx += 1
  }

  return keyStr
}

// ─── BakePauseController ─────────────────────────────────────────────────────

/**
 * styleToInlineStyleAsync 의 일시정지 게이트(BakePauseGate) 구현. `pause()` 로 닫으면 진행 중인
 * 변환이 다음 yield 경계에서 멈춰 대기하고(promise pending), `resume()` 로 열면 이어서 진행한다.
 * 작업은 지연될 뿐 소멸하지 않으므로 깨진 상태가 구조적으로 불가능하다. 에디터 탭 활성화 중 뷰어
 * bake 가 메인 스레드를 경합하지 않도록 host 가 탭 전환에 맞춰 열고 닫는다. 하나의 인스턴스를 뷰어의
 * 모든 bake 가 공유한다(viewer 싱글턴).
 */
export class BakePauseController implements BakePauseGate {
  private _paused = false
  // resume/abort 로 깨울 대기자들. abort 시에는 해당 대기자만 제거(reject 는 waitWhilePaused 가 수행).
  private waiters: Array<() => void> = []

  get paused(): boolean {
    return this._paused
  }

  pause(): void {
    this._paused = true
  }

  resume(): void {
    if (this._paused === false) return
    this._paused = false
    const pending = this.waiters
    this.waiters = []
    pending.forEach((wake) => wake())
  }

  waitWhilePaused(signal?: AbortSignal): Promise<void> {
    if (this._paused === false) return Promise.resolve()
    if (signal?.aborted === true) {
      return Promise.reject(new DOMException('Aborted', 'AbortError'))
    }
    return new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        const idx = this.waiters.indexOf(wake)
        if (idx >= 0) this.waiters.splice(idx, 1)
        reject(new DOMException('Aborted', 'AbortError'))
      }
      const wake = () => {
        if (signal != null) signal.removeEventListener('abort', onAbort)
        resolve()
      }
      this.waiters.push(wake)
      if (signal != null) signal.addEventListener('abort', onAbort, { once: true })
    })
  }
}

// 주입 의사요소 <style> 식별 마커(SoT: libs/style-to-inline/markers). 기존 import 경로 호환을 위해 re-export.
export { PSEUDO_STYLE_MARKER }

interface GetSafeHTMLOptions {
  processElement?: (elem: HTMLElement) => void
  isAllowed?: (elem: HTMLElement) => boolean
  // 지정 시 툴바 인용구(variant) 의사요소 장식을 뷰어에 보존하는 <style> 을 결과에 주입한다(email id 기준 scope).
  pseudoScopeId?: string
  // OMD-29: bake 시점 iframe CSSOM 의 top-level @media condition 목록과 remSize(px) 를 노출한다.
  // 리사이즈 시 media query 재반영을 위한 width breakpoint 파싱에만 쓰이며 결과 HTML 에는 영향이 없다.
  onMediaConditions?: (conditionTexts: string[], remSize: number) => void
  // 지정 시, 비싼 변환(styleToInlineStyle)을 시작하는 매크로태스크 진입 시점에 중단 여부를 확인해
  // 이미 중단됐으면 변환을 생략하고 AbortError 로 reject 한다. 다른 메일로 이동해 뷰가 언마운트되면
  // 뒤늦게 도착한 본문의 변환이 메인 스레드를 블록(freeze)하는 것을 막는 데 쓴다.
  signal?: AbortSignal
  // 이메일이 자체 선언한 prefers-color-scheme 테마 분기를 제거해 bake 결과를 light 기준으로
  // 고정한다(기본 true). dark @media 는 삭제, light @media 는 제자리에 펼친다. 다크 표시는
  // 뷰어의 darkmode 모듈(mailview)이 전담한다. removePrefersColorSchemeRules 참고.
  lightThemeOnly?: boolean
  // true 면 getSafeHTML 문자열 경로도 뷰어(getSafeElement)와 동일한 time-sliced 변환
  // (styleToInlineStyleAsync)을 사용한다 — 대형 본문에서 메인 스레드 freeze 없이 bake.
  // signal 과 병용을 권장(중단 시 AbortError 로 reject). 미지정(기본)은 기존 동기 변환.
  // getSafeElement 는 항상 time-sliced 로 동작하며 이 옵션으로 끌 수 없다.
  asyncYield?: boolean
  // dir="auto" 처리 방식 명시 override. 미지정 시 소비 형태 기본값(getSafeElement=resolve,
  // getSafeHTML=preserve)을 따른다. 에디터가 getSafeElement(Fragment)를 쓰면서도 dir="auto" 를
  // 보존해야 하는 경로(reply 인용 DOM 직결)에서 'preserve' 로 명시한다.
  dirAutoMode?: 'resolve' | 'preserve'
  // getSafeElement 전용 — true 면 bake 된 최상위 래퍼(root element)를 벗기지 않고 그대로 fragment 에
  // 담는다. 뷰어(기본 false)는 wrapper 를 벗겨 자식만 .htmlMailBody 에 붙이지만, 에디터 인용/서명
  // 주입은 getSafeHTML(문자열) 경로와 동일하게 최상위 래퍼(기준 font-family/color/line-height 보유)를
  // 보존해야 PM 파싱 결과가 동일해진다.
  keepRoot?: boolean
  // time-sliced 변환 전용 — 지정 시 yield 경계에서 일시정지 게이트를 확인해, 닫혀 있으면 열릴
  // 때까지 변환을 미룬다(abort 우선). 에디터 탭 활성화 중 뷰어 bake 가 메인 스레드를 경합하지 않게
  // 한다. 동기 변환(인쇄/서명)에는 영향이 없다. BakePauseController 참고.
  pauseGate?: BakePauseGate
  // 지정 시 메일 본문 iframe 을 호출부 정책으로 처리한다(미지정이면 iframe 미관여 — web 기본).
  // 뷰어는 settings 기반 makeMailBodyIframeSanitizer 로 생성해 전달한다.
  // → packages/email-thread-viewer/src/EmailView/mailBodyIframeSanitizer.ts.
  mailBodyIframeSanitizer?: (iframe: HTMLIFrameElement) => void
}

// script/on*/javascript: 외에 잔존할 수 있는 인라인 이벤트 핸들러(프로퍼티 + on* 속성)를 제거한다.
// getSafeHTML / getSafeElement 두 파이프라인이 동일 규칙을 공유한다.
function clearElementEventHandlers(elem: HTMLElement): void {
  // 기존 이벤트 핸들러 제거
  ;(elem as any).onclick = ''
  ;(elem as any).onmousedown = ''
  ;(elem as any).onmouseup = ''
  ;(elem as any).onmousemove = ''
  ;(elem as any).onkeypress = ''
  ;(elem as any).onkeydown = ''
  ;(elem as any).onkeyup = ''
  ;(elem as any).onfocus = ''
  ;(elem as any).onblur = ''
  ;(elem as any).onselect = ''
  ;(elem as any).ondrag = ''
  ;(elem as any).ondrop = ''
  ;(elem as any).oncopy = ''
  ;(elem as any).oncut = ''
  ;(elem as any).onpaste = ''
  ;(elem as any).onchange = ''
  ;(elem as any).onerror = ''

  // 특히 위험한 누락 이벤트 핸들러 추가 제거
  ;(elem as any).onload = ''
  ;(elem as any).onunload = ''
  ;(elem as any).onbeforeunload = ''
  ;(elem as any).ondblclick = ''
  ;(elem as any).onmouseover = ''
  ;(elem as any).onmouseout = ''
  ;(elem as any).oncontextmenu = ''
  ;(elem as any).oninput = ''
  ;(elem as any).onsubmit = ''
  ;(elem as any).onreset = ''
  ;(elem as any).onabort = ''
  ;(elem as any).onmessage = ''

  // on* 속성 일괄 제거 (미래 이벤트 대응)
  const attributes = elem.attributes
  for (let i = attributes.length - 1; i >= 0; i--) {
    const attrName = attributes[i].name
    if (attrName.length > 2 && attrName.startsWith('on')) {
      elem.removeAttribute(attrName)
    }
  }
}

/**
 * iframe 문서 안의 `<img>` 를 분리(src/srcset 제거 + remove)해 detached 문서가 GC 되도록 한다.
 *
 * ⚠️ 삭제 금지 — 이 로직은 Chrome(Chromium) 메모리 누수 대응이다. 제거하면 누수가 되살아난다.
 *
 * 배경:
 * - Chromium 은 iframe/문서를 DOM 에서 떼어내도(detached), 그 안의 `<img>` 가 로더의 pending
 *   활동(C++ roots)에 참조로 남아 **detached document 전체가 GC 되지 못하는** 누수가 있다.
 *   특히 `loading="lazy"` 이미지에서 두드러진다(Chromium issue 40183465). DevTools Memory
 *   heap snapshot 에서 "Detached HTMLImageElement" 의 유일한 retainer 가
 *   `Internal Node → Pending activities → C++ roots` 로 잡히는 그 케이스다.
 * - 이 함수의 `removeAttribute('src'/'srcset')` 가 그 pending 참조를 끊어 문서를 회수시킨다.
 *   (뷰어는 bake 단계에서 모든 img 를 `loading="eager"` 로 강제해 lazy 누수 자체도 예방한다.)
 *
 * 발견 경위: detached 노드 추적(Detached elements profile / heap snapshot retainer)은 Chrome
 *   DevTools 전용이라, 이 누수는 Chrome 에서 디버깅해 확인·수정했다. → thread-viewer R17~R23, R26.
 *
 * ⚠️ Safari 와 혼동 주의: macOS 27 WebKit 에서 보고된 "뷰어 외부 이미지 깨짐"은 이 함수가 아니라
 *   임시 bake iframe 제거(iframe.remove) 시 미완료 `<img>` 로드가 취소되고 복구되지 않는 WebKit
 *   동작 변경이 원인이다(실기 확정, COEP 무관). 그 증상을 고치겠다고 이 누수 방지 로직을 건드리지
 *   말 것 — 복구는 bake 결과를 뷰어 문서에 붙인 직후 src/srcset 재지정으로 처리한다
 *   (EmailView restartBrokenImageLoads, WebKit 엔진 한정).
 */
export function detachIFrameImages(
  iframe: HTMLIFrameElement | null | undefined,
  opts?: { clearDocument?: boolean },
): void {
  if (iframe == null) return
  try {
    const cdoc = iframe.contentDocument
    if (cdoc != null) {
      cdoc.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('src')
        img.removeAttribute('srcset')
        img.remove()
      })
      if (opts?.clearDocument === true) {
        cdoc.documentElement?.replaceChildren()
      }
    }
  } catch (e) {
    console.warn('[detachIFrameImages] 정리 실패:', e)
  }
}

// html(문자열 또는 파싱된 Document)을 temp iframe 에서 inline style 로 bake 한 뒤, 완성된 본문
// 클론(bodyElem)을 finalize 콜백에 넘겨 소비 형식(HTML 문자열 / DOM)을 결정한다. getSafeHTML 과
// getSafeElement 가 이 코어를 공유한다. Document 를 넘기면 내부 DOMParser 재파싱을 생략한다
// (OFM-221: 동일 2MB HTML 중복 파싱 제거). Document 는 sanitize 로 mutate 되므로 호출부는 전달 후
// 해당 Document 를 재사용하지 않아야 한다.
// asyncYield(파라미터): true 면 getSafeElement 호출 — 산출물이 정적 스냅샷(뷰어)임을 뜻하며
// dirAutoMode=resolve 의 기준이 된다. 변환 자체의 time-slicing 여부는 이 파라미터 또는
// opts.asyncYield(getSafeHTML opt-in) 둘 중 하나라도 true 면 styleToInlineStyleAsync(시간기반
// yield)로 수행해 대형 메일에서도 메인 스레드를 장시간 블록하지 않는다. 청크 사이 abort 시
// AbortError 로 reject 한다. 둘 다 false(기본)면 기존 동기 변환 — 인쇄/서명 경로 무변경.
function runSafeBodyPipeline<T>(
  html: string | Document,
  options: GetSafeHTMLOptions | string | undefined,
  width: string | undefined,
  finalize: (bodyElem: HTMLElement | SVGElement) => T,
  asyncYield = false,
): Promise<T> {
  const promise = new Promise<T>((resolve, reject) => {
    let opts: GetSafeHTMLOptions = {}
    if (typeof options === 'object') {
      opts = options
    }

    // time-sliced 변환 여부. dirAutoMode 는 산출물 소비 형태(정적 스냅샷 vs 편집 가능)에 결합된
    // 값이라 asyncYield 파라미터만 보고, time-slicing 은 opts.asyncYield opt-in 을 함께 본다 —
    // 에디터 인용 bake(getSafeHTML + opts.asyncYield)는 time-sliced 이면서 dir="auto" 를 보존한다.
    const timeSliced = asyncYield === true || opts.asyncYield === true

    // 호출 시점에 이미 중단됐으면 어떤 작업도 시작하지 않는다.
    if (opts.signal?.aborted === true) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    // OFM-221: Document 를 받으면 재파싱 생략(중복 파싱 제거). 문자열이면 기존대로 파싱한다.
    const docWithoutUI =
      typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html
    // active iframe 에 넣기 전 script/on*/javascript: 제거. opts.mailBodyIframeSanitizer 지정 시
    // (뷰어) 메일 본문 iframe 도 호출부 정책으로 처리 — 미지정(web/compose 경로)이면 iframe 미관여.
    sanitizeHtmlForIframeWrite(docWithoutUI, opts.mailBodyIframeSanitizer)
    const styles = docWithoutUI.querySelectorAll('style')
    cssImportToLink(styles as any, docWithoutUI)
    const iframe = createTempHiddenIFrame()
    // 기본 폭 720px: 테스트 샘플에서 media query가 700px를 모바일 분기 기준으로 쓰는 케이스가
    // 있어, 기본값을 700px로 두면 일반(데스크톱) 레이아웃이 아닌 모바일 레이아웃으로 인라인된다.
    // 720px로 올려 해당 경계를 넘겨 일반 크기로 처리되도록 한다.
    iframe.style.width = width || '720px'

    const doc = iframe.contentDocument!

    // OFM-221: doc.write 재파싱 + outerHTML 재직렬화 대신, 파싱·sanitize 완료된 docWithoutUI 의
    // documentElement 를 iframe 문서로 adopt(이동) 후 root 교체 → 동일 HTML 의 재파싱을 제거한다.
    // sanitizeHtmlForIframeWrite 에서 script/noscript 를 이미 제거하므로 active context 로 옮겨도
    // 스크립트 실행 위험이 없다. <style> 은 연결 시 CSSOM 이 살아나 getRuleMap(styleSheets) 이 동작한다.
    const adoptedRoot = doc.adoptNode(docWithoutUI.documentElement)
    doc.replaceChild(adoptedRoot, doc.documentElement)

    // lightThemeOnly(기본 true): adoptNode 로 CSSOM 이 살아난 직후·remSize 계산 전에 테마 분기를
    // 제거해, dark @media 가 root font-size 나 computed style 에 반영되기 전에 차단한다.
    if (opts.lightThemeOnly !== false) {
      removePrefersColorSchemeRules(doc)
      // light-dark() 함수와 UA 기본색(canvas/text)의 OS 의존 제거. 이메일 자체의 meta/:root
      // color-scheme 선언보다 inline !important 가 우선한다. 이 지정은 media query(matchMedia)
      // 평가에는 영향이 없다 — @media 분기 차단은 removePrefersColorSchemeRules 가 담당한다.
      // 출력은 body 클론이라 이 지정이 결과물에 남지 않는다.
      doc.documentElement.style.setProperty('color-scheme', 'light', 'important')
    }

    let remSize = 16
    const rootFontSize = iframe.contentWindow!.getComputedStyle(doc.documentElement, null).fontSize
    const rootFSValue = getCSSValue(rootFontSize, false)
    if (rootFSValue.type == 1 && rootFSValue.isRel === false) {
      remSize = rootFSValue.number!
    }

    // OMD-29: adoptNode 로 CSSOM 이 살아난 상태에서 top-level @media condition 을 수집해 호출부로
    // 넘긴다(리사이즈 재계산용 breakpoint 파싱). getRuleMap 과 동일하게 접근 불가 sheet 는 skip.
    if (opts.onMediaConditions != null) {
      const conditionTexts: string[] = []
      for (const sheet of Array.from(doc.styleSheets)) {
        let rules: CSSRuleList
        try {
          rules = sheet.cssRules
        } catch {
          continue
        }
        for (const rule of Array.from(rules)) {
          if (rule.constructor.name === 'CSSMediaRule') {
            conditionTexts.push((rule as CSSMediaRule).conditionText)
          }
        }
      }
      opts.onMediaConditions(conditionTexts, remSize)
    }

    const inlineOptions = {
      recursive: true,
      // properties: 'color background background-color font-weight border border-top border-left padding padding-left padding-top margin margin-left margin-right margin-top margin-bottom width height max-width max-height'.split(' '),
      win: iframe.contentWindow!,
      remSize, // root font size를 px 단위로 전달.
      clsPrefix: 'x_',
      idPrefix: 'x_',
      // 뷰어 경로(pseudoScopeId 전달 시)만 의사요소 <style> 주입. clsPrefix('x_') 와 동일 규칙으로 재작성.
      pseudoScopeId: opts.pseudoScopeId,
      pseudoStyleMarker: PSEUDO_STYLE_MARKER,
      // dir="auto" 컨테이너의 O(n²) 조립 비용 제거(StyleToInlineOptions.dirAutoMode 참고).
      // 뷰어(getSafeElement, asyncYield)는 정적 스냅샷이라 계산된 방향으로 고정(resolve)하고,
      // 편집기/서명(getSafeHTML)은 이후 내용 수정 시 방향이 자동으로 따라가야 하므로 dir="auto" 를
      // 보존(preserve — 조립 후 재부착)한다.
      dirAutoMode:
        opts.dirAutoMode ?? (asyncYield === true ? ('resolve' as const) : ('preserve' as const)),
      processElement: (elem: Element) => {
        clearElementEventHandlers(elem as HTMLElement)

        elem.removeAttribute('contenteditable')

        if (opts.processElement != null) {
          opts.processElement(elem as HTMLElement)
        }
      },
      shorthandCheck: (element: Element, prop: string) => {
        if (shorthandStyleMap[prop] != null) {
          let shorthandProp = prop
          let shorthandValue: string | null = null

          let value: string
          const htmlElement = element as HTMLElement
          do {
            if (shorthandStyleMap[shorthandProp] != null) {
              value = htmlElement.style.getPropertyValue(shorthandStyleMap[shorthandProp])
              if (value != null && value.length > 0) {
                shorthandProp = shorthandStyleMap[shorthandProp]
                shorthandValue = value
              }
            } else {
              break
            }
          } while (value != null && value.length > 0)

          if (shorthandValue != null && shorthandValue.length > 0) {
            return {
              prop: shorthandProp,
              value: htmlElement.style.getPropertyValue(shorthandProp),
              isImportant: htmlElement.style.getPropertyPriority(shorthandProp) === 'important',
            }
          }
        }
        return null
      },
    }

    // 변환 완료된 본문 클론의 공통 후처리: script/<style> 제거 후 finalize 로 소비 형식 결정.
    const finishBody = (bodyElem: HTMLElement | SVGElement) => {
      bodyElem.querySelectorAll('script').forEach((elem) => {
        elem.parentElement!.removeChild(elem)
      })

      bodyElem.querySelectorAll('style').forEach((elem) => {
        // 의사요소 보존용 주입 <style>(마커 부착)은 유지 — 인라인화 불가한 ::before/::after 장식을 담고 있다.
        if (elem.hasAttribute(PSEUDO_STYLE_MARKER)) return
        elem.parentElement!.removeChild(elem)
      })

      // 완성된 본문 클론을 finalize 에 넘겨 소비 형식(HTML 문자열 / DOM)을 결정한다.
      resolve(finalize(bodyElem))
    }

    // temp 문서를 통째로 버리므로 문서 트리의 img까지 분리(clearDocument:true) 후 iframe 제거.
    const cleanupIframe = () => {
      detachIFrameImages(iframe, { clearDocument: true })
      iframe.remove()
    }

    // 처리 직후 temp iframe을 제거 — 메일 콘텐츠를 담은 iframe이 body에 상주하지 않도록 한다.
    const finishDoc = () => {
      // setTimeout 없이 바로 호출하면, link 태그로 로딩된 stylesheet가 document.styleSheets에 반영되기 전에 processDoc()가 호출되어 inline style이 누락되는 문제가 발생한다.
      setTimeout(() => {
        // 비싼 변환(styleToInlineStyle)에 진입하기 직전 중단 여부를 확인한다. 이미 중단됐으면
        // 변환을 생략해 메인 스레드 블록(freeze)을 피하고, iframe 만 정리한 뒤 AbortError 로 reject 한다.
        if (opts.signal?.aborted === true) {
          cleanupIframe()
          reject(new DOMException('Aborted', 'AbortError'))
          return
        }
        if (timeSliced === true) {
          // time-sliced 경로(뷰어 전체 + 에디터 인용 opt-in): 시간기반 yield 변환. 청크 사이
          // abort(AbortError) 포함 모든 오류를 reject 로 전달하고, 어느 경우든 iframe 은 반드시
          // 정리한다. 변환 중 iframe 은 live CSSOM(matchRules/element.matches) 때문에 유지되어야
          // 하므로 완료 후에만 제거한다.
          styleToInlineStyleAsync(doc.body, {
            ...inlineOptions,
            signal: opts.signal,
            pauseGate: opts.pauseGate,
          })
            .then(finishBody, reject)
            .finally(cleanupIframe)
          return
        }
        try {
          finishBody(styleToInlineStyle(doc.body, inlineOptions))
        } catch (error) {
          // timer 안의 예외는 Promise executor로 전파되지 않아 직접 reject해야 한다.
          reject(error)
        } finally {
          cleanupIframe()
        }
      }, 1)
    }

    if (doc.readyState === 'complete' || doc.readyState === 'interactive') {
      finishDoc()
    } else {
      const onLoaded = () => {
        doc.removeEventListener('DOMContentLoaded', onLoaded)
        finishDoc()
      }
      doc.addEventListener('DOMContentLoaded', onLoaded)
    }
  })

  return promise
}

// html 을 sanitize/inline bake 한 뒤 HTML 문자열로 직렬화해 반환한다. 소비측은 innerHTML 로 주입한다.
// Document 를 넘기면 내부 DOMParser 재파싱을 생략한다(OFM-221). Document 는 sanitize 로 mutate 되므로
// 호출부는 전달 후 해당 Document 를 재사용하지 않아야 한다.
export function getSafeHTML(
  html: string | Document,
  options?: GetSafeHTMLOptions | string,
  width?: string,
): Promise<string> {
  return runSafeBodyPipeline(html, options, width, (bodyElem) => {
    // 결과 문자열을 먼저 캡처(src 보존)한 뒤, 클론 트리의 img에서 리소스 참조를 끊는다.
    // styleToInlineStyle이 cloneNode로 만든 클론 img는 temp iframe 문서 소유라
    // src가 살아있으면 temp 문서를 active로 잡아 GC를 막는다. (네트워크 fetch가
    // 완료될 수는 있으나 active wrappable 참조 사슬이 끊겨 문서는 회수 가능해진다.)
    const resultHTML = bodyElem.outerHTML
    bodyElem.querySelectorAll('img').forEach((img) => {
      img.removeAttribute('src')
      img.removeAttribute('srcset')
      img.remove()
    })
    return resultHTML
  })
}

// getSafeHTML 과 동일 파이프라인으로 bake 하되, 직렬화된 HTML 문자열이 아니라 완성된 DOM 을 직접
// 반환한다. 소비측(이메일 뷰어)이 문자열을 다시 파싱(innerHTML)하지 않고 ref.appendChild 로 바로
// 삽입할 수 있어 "DOM→문자열→DOM" 왕복 중 재파싱 단계를 제거한다. 본문(body clone)의 자식들을
// 메인 문서 소유 DocumentFragment 로 이동(adopt)하므로, 반환된 fragment 를 appendChild 하면 그대로
// 화면에 붙고 temp iframe 문서 참조도 끊긴다(별도 img src 제거가 불필요). innerHTML 주입 시 <body>
// 래퍼가 소실되던 기존 동작과 동일하게, body 래퍼는 벗기고 자식들만 넘긴다.
export function getSafeElement(
  html: string | Document,
  options?: GetSafeHTMLOptions | string,
  width?: string,
): Promise<DocumentFragment> {
  return runSafeBodyPipeline(
    html,
    options,
    width,
    (bodyElem) => {
      const fragment = document.createDocumentFragment()
      // keepRoot: bake 된 최상위 래퍼(기준 폰트/색/줄간격 보유)를 보존해야 하는 에디터 경로
      // (reply 인용/서명 DOM 직결). 미지정(뷰어)은 래퍼를 벗기고 자식만 넘긴다.
      if (typeof options === 'object' && options?.keepRoot === true) {
        fragment.appendChild(bodyElem)
        return fragment
      }
      // draft-body-style-fidelity(뷰어): 메일 본문의 기준 서식(font-family/font-size/line-height/
      // color)은 `<body nine-mail-id style="…">` 래퍼의 inline style 한 곳에만 있고 자식 요소는
      // CSS 상속으로 이를 받는다 — styleToInlineStyle 은 매칭 규칙·inline style 만 인라인화하고
      // 상속 속성을 자식마다 중복 기입하지 않기 때문이다. 래퍼를 그대로 벗기면 상속 체인이 끊겨
      // 본문이 앱 타이포(.htmlMailBody 의 font-size:initial 등)로 폴백된다(= 과거 nine-mail-id
      // 래퍼 소실 회귀). 래퍼의 속성(style·nine-mail-id·dir 등)을 그대로 옮긴 div 로 자식을 감싸
      // 상속을 복원한다. 서식도 마커도 없는 외부메일은 불필요한 래퍼를 만들지 않는다(기존 동작 유지).
      const rootEl = bodyElem as HTMLElement
      const hasAttrs = typeof rootEl.getAttribute === 'function' && rootEl.attributes.length > 0
      if (hasAttrs) {
        const wrapper = document.createElement('div')
        for (const attr of Array.from(rootEl.attributes)) {
          wrapper.setAttribute(attr.name, attr.value)
        }
        while (bodyElem.firstChild != null) {
          wrapper.appendChild(bodyElem.firstChild)
        }
        fragment.appendChild(wrapper)
        return fragment
      }
      while (bodyElem.firstChild != null) {
        fragment.appendChild(bodyElem.firstChild)
      }
      return fragment
    },
    // 뷰어 전용: 변환을 시간기반 yield 로 쪼개 대형 메일 로딩 중에도 입력(다른 메일 선택 등)이
    // 처리되게 한다. 인쇄/서명/에디터(getSafeHTML)는 동기 변환 유지.
    true,
  )
}
